/**
 * i18n runtime (spec §4.8): `useT()` for UI strings, `useLang()` for the toggle.
 *
 * The provider is given the already-merged dictionaries by `app/providers.tsx`
 * (core + every feature's `dict`), which keeps this module free of any import
 * of the feature registry.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Lang } from "@shared/api";
import { interpolate, pluralKey, type Dictionary, type LocalizedDictionary } from "./merge";
import type { CoreKey } from "./sv";

export const LANG_STORAGE_KEY = "mf.lang";
export const LANGS: Lang[] = ["sv", "en"];

/**
 * 2026-09-28: the app is Swedish only; English dictionaries stay for tests and
 * a later return. `main.tsx` forces `lang="sv"`, and the language toggle and
 * the Settings language select render only when this is true.
 */
export const LANGUAGE_SWITCH_ENABLED = false;

/** Core keys autocomplete; feature keys (any string) are accepted too. */
export type TKey = CoreKey | (string & Record<never, never>);
export type TFunction = (key: TKey, vars?: Record<string, string | number>) => string;
/**
 * Count-aware translation: `tp("progress.stats.streak.value", 1)` reads
 * `progress.stats.streak.value.one`, any other count `….other`. `{count}` is
 * interpolated for you; pass it in `vars` to override it with a formatted
 * number (`formatNumber`) while the raw count still picks the form.
 */
export type TPluralFunction = (
  key: TKey,
  count: number,
  vars?: Record<string, string | number>,
) => string;

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
  tp: TPluralFunction;
  dictionary: Dictionary;
}

const I18nContext = createContext<I18nValue | null>(null);

export function readStoredLang(): Lang | null {
  try {
    const raw = window.localStorage.getItem(LANG_STORAGE_KEY);
    return raw === "sv" || raw === "en" ? raw : null;
  } catch {
    return null;
  }
}

function storeLang(lang: Lang): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* private mode — the language simply is not remembered */
  }
}

export interface I18nProviderProps {
  dictionaries: LocalizedDictionary;
  /** Overrides the stored language (tests, or a user whose profile says otherwise). */
  lang?: Lang;
  children: ReactNode;
}

export function I18nProvider({ dictionaries, lang: forcedLang, children }: I18nProviderProps) {
  const [chosenLang, setLangState] = useState<Lang>(() => forcedLang ?? readStoredLang() ?? "sv");

  // `lang` is a *controlled* prop when given: it outranks the stored language
  // and the profile language that `AuthProvider` adopts once `/api/auth/me`
  // resolves. Without this the adoption races the render and a test that asks
  // for English gets Swedish back the moment the stubbed user (language "sv")
  // arrives.
  const lang = forcedLang ?? chosenLang;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    storeLang(next);
    setLangState(next);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dictionary = dictionaries[lang];
    const fallback = dictionaries.sv;
    const lookup = (key: string): string | undefined => dictionary[key] ?? fallback[key];
    const t: TFunction = (key, vars) => interpolate(lookup(key) ?? String(key), vars);
    // A dictionary that never grew the `.one`/`.other` pair falls back to the
    // bare key, so plural-ising a string is a dictionary change, not a
    // call-site migration.
    const tp: TPluralFunction = (key, count, vars) =>
      interpolate(lookup(pluralKey(key, count)) ?? lookup(key) ?? String(key), {
        count,
        ...vars,
      });
    return { lang, setLang, t, tp, dictionary };
  }, [dictionaries, lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT/useLang must be used inside <I18nProvider>");
  return ctx;
}

/** The translate function. */
export function useT(): TFunction {
  return useI18n().t;
}

/** The count-aware translate function — see `TPluralFunction`. */
export function useTp(): TPluralFunction {
  return useI18n().tp;
}

/** The active language and the setter behind the toggle. */
export function useLang(): { lang: Lang; setLang: (lang: Lang) => void; toggle: () => void } {
  const { lang, setLang } = useI18n();
  return { lang, setLang, toggle: () => setLang(lang === "sv" ? "en" : "sv") };
}

export { mergeDictionaries, duplicateKeys, keyParityGaps, pluralKey } from "./merge";
export type { Dictionary, LocalizedDictionary } from "./merge";
export { sv } from "./sv";
export { en } from "./en";
export type { CoreDictionary, CoreKey } from "./sv";
